import { HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppSettings } from 'src/app/app.settings';
import { Settings } from './../../app.settings.model';
import { AttachmentDialogService } from './attachment-dialog.service';
import { Attachment, AttachmentFilter } from './attachment.model';

import { MessageDialogService } from 'src/app/shared-component/services/message-dialog.service';
@Component({
    selector: 'app-attachment-dialog',
    templateUrl: './attachment-dialog.component.html',
    styleUrls: ['./attachment-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AttachmentDialogComponent implements OnInit, OnDestroy {
  image: boolean;
  video: boolean;
  audio: boolean;
  document: boolean;
  text: boolean;
  public progress: number;
  public progressBuffer: number;
  public message: string;
  readyToSave: boolean;
  formData: FormData;
  @Output() public onUploadFinished = new EventEmitter();
  @Input() formControl!: UntypedFormControl;
  attachmentsList: Attachment[] = new Array<Attachment>();
  public settings: Settings;
  previewType: string;
  previewUrl: string;
  caption: string;
  filesCount: number = 0;

  private destroy$ = new Subject<void>();


  constructor(public dialogRef: MatDialogRef<AttachmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttachmentFilter,
    public uploadService: AttachmentDialogService,
    private translate: TranslateService,
    private messageDialogService: MessageDialogService,
    private cdr: ChangeDetectorRef,
    public appSettings: AppSettings) {
    this.settings = this.appSettings.settings;
  }

  ngOnInit(): void {
    let types: Array<string> = this.data.fileType.split('|');
    this.image = types.indexOf('image') >= 0;
    this.video = types.indexOf('video') >= 0;
    this.audio = types.indexOf('audio') >= 0;
    this.document = types.indexOf('document') >= 0;
    this.text = types.indexOf('text') >= 0;
    this.readyToSave = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  readonly MAX_FILE_SIZE_BYTES: number = 100 * 1024 * 1024; // 100 MB

  public onFileSelected = (event) => {
    this.attachmentsList = new Array<Attachment>();
    this.readyToSave = false;
    this.formData = new FormData();
    let files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    for (let index = 0; index < files.length; index++) {
      let fileToUpload = <File>files[index];
      if (fileToUpload.size > this.MAX_FILE_SIZE_BYTES) {
        event.target.value = '';
        this.filesCount = 0;
        this.formData = new FormData();
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
          this.previewType = null;
        }
        const alertMsg = this.translate.instant('File size exceeds the maximum limit of 100 MB') ||
          `The selected file exceeds the maximum allowed limit of 100 MB.`;
        this.messageDialogService.open(alertMsg, this.translate.instant('Warning') || 'Warning');
        this.cdr.markForCheck();
        return;
      }
    }

    this.filesCount = files.length;

    if (this.data.preview && !this.data.multiFiles) {
      this.genaratePreview(event.target.files[0]);
    }

    for (let index = 0; index < files.length; index++) {
      let fileToUpload = <File>files[index];
      this.formData.append('file', fileToUpload, fileToUpload.name);
    }
    this.cdr.markForCheck();
  }

  upload() {
    if (this.formData.has("file")) {
      this.uploadService.uploadFile(this.formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.progress = Math.round(100 * event.loaded / event.total);
              this.progressBuffer = this.progress + 20;
              this.cdr.markForCheck();
            }
            else if (event.type == HttpEventType.Response) {
              this.message = this.translate.instant('Upload done successfully');
              event.body.forEach(item => {
                let attach = new Attachment();
                Object.assign(attach, item);
                attach.caption = this.caption;
                this.attachmentsList.push(attach);
              });
              this.readyToSave = true;
              this.onUploadFinished.emit(event.body);
              this.cdr.markForCheck();
              this.dialogRef.close(this.attachmentsList);
            }
          },
          error: (err) => {
            this.progress = 0;
            this.progressBuffer = 0;
            const errorMsg = err?.status === 413
              ? this.translate.instant('File size exceeds the maximum limit of 100 MB')
              : (this.translate.instant('Upload failed') || 'Upload failed');
            this.messageDialogService.open(errorMsg, this.translate.instant('Error') || 'Error');
            this.cdr.markForCheck();
          }
        });
    }
  }

  genaratePreview(file) {
    let fr = new FileReader();
    fr.readAsArrayBuffer(file);
    fr.onload = () => {
      const blob = new Blob([fr.result], { type: file.type });
      this.previewType = blob.type.split('/')[0];
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = URL.createObjectURL(blob);
      this.cdr.markForCheck();
    }
  }

  showAllowedFilesExtensions() {
    this.messageDialogService.open('File : .pdf | .doc | .docx | .xls | .xlsx | .ppt | .pptx | .txt ' +
      '<br /><br />' + 'Image : .jpg | .jpeg | .png | .jfif | .bmp | .webp | .apng ' +
      '<br /><br />' + 'Video : .mp4 | .mov | .wmv | .avi | .webm | .mkv ' +
      '<br /><br />' + 'Audio : .mp3 | .ogg | .wma | .wav ' +
      '<br /><br /><strong style="color: #b45309;">' + this.translate.instant('Max file size: 100 MB') + '</strong>');
  }


}
